import * as React from 'react';
import { Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { NextSeo } from 'next-seo';
import { SectionContainer } from '@/components/LandingPage/Components/styles';

import Header from '../components/LandingPage/Header';
import Footer from '../components/LandingPage/Footer';
import ScrollToTop from '../components/LandingPage/Components/ScrollToTop';

const termsSections = [
    {
        title: "Introduction",
        content: () => (
            <span>
                Welcome to Wave Horizons LTD. By navigating our website, Scriptu, available at&nbsp;
                <a href="https://www.scriptu.io">https://www.scriptu.io</a>, you enter into a binding agreement with us, outlined by these Terms and Conditions and our Privacy Policy (accessible at&nbsp;
                <a href={`${process.env.NEXT_PUBLIC_BASE_URL}/privacy`}>{`${process.env.NEXT_PUBLIC_BASE_URL}/privacy`}</a>).
                Should you find these terms unsatisfactory, please contact us at&nbsp;
                <a href="mailto:contact@scriptu.io">contact@scriptu.io</a> to discuss potential modifications.
            </span>
        )
    },
    {
        title: "Communication Consent",
        content: "Registering on our platform implies your consent to receive periodic communications from us, including updates and promotional content. If you prefer not to receive these communications, we provide easy opt-out instructions with each message."
    },
    {
        title: "Purchasing Terms",
        content: "When making a purchase through Scriptu, certain information will be required for transaction completion. Transactions are facilitated by third-party processors, adhering to our Privacy Policy. We reserve the right to cancel transactions based on certain criteria, including but not limited to inaccuracies, suspected fraud, or misuse of our services."
    },
    {
        title: "Subscription Details",
        content: "Our services may include subscription-based offerings, billed according to the selected cycle. Subscriptions auto-renew under the same conditions unless altered or cancelled by either party. Should automatic billing fail, we will request manual payment completion."
    },
    {
        title: "Trial Offers and Pricing Changes",
        content: "We may introduce trial offers for our services, which are subject to adjustments or cancellation. Any changes in subscription pricing will be communicated in advance, allowing you the opportunity to cancel prior to the new pricing taking effect."
    },
    {
        title: "Refund Policy",
        content: "We accommodate refund requests within 30 days of the initial purchase, subject to specific conditions. Please contact our support for more information."
    },
    {
        title: "Lifetime Deal Terms and Refunds",
        content:
            "Scriptu.io offers limited-time Lifetime Deal (LTD) plans which grant customers perpetual access to the selected tier of Scriptu features for a one-time payment. LTD plans are non-renewable and do not require monthly or annual payments. Every LTD purchase is covered by a 30-day no-questions-asked refund policy. If you're not satisfied for any reason, you may request a full refund within 30 days of the original transaction date. Refunds are processed via Stripe and must be initiated by contacting our support team at contact@scriptu.io. After the 30-day window, LTD purchases become non-refundable. Abuse of this policy may result in account restrictions or LTD access revocation."
    },
    {
        title: "Content Responsibilities",
        content: "You hold responsibility for the legality and ownership of any content you contribute to our platform. By sharing content, you grant us the rights for its use and distribution as outlined in these terms."
    },
    {
        title: "Acceptable Use Policy",
        content: "Our platform is designed for lawful use, avoiding any activities that could harm or exploit others. We enforce strict guidelines to maintain a safe and respectful environment."
    },
    {
        title: "Analytics and Data Use",
        content: "We employ third-party services, including Google Analytics and Amplitude, for insights into our service usage. While some analytics features may offer opt-out capabilities, our use of Amplitude is designed to improve user experience and may not provide an opt-out option."
    },
    {
        title: "Age Restrictions",
        content: "Access to and use of Scriptu is intended for individuals aged 18 and above. By using our services, you affirm that you meet this age requirement."
    },
    {
        title: "Account Integrity",
        content: "You are responsible for the accuracy and security of your account information. Inaccurate or outdated information may lead to account suspension."
    },
    {
        title: "Intellectual Property",
        content: "All content and functionalities provided by Wave Horizons LTD through Scriptu are protected by copyright laws. Unauthorized use is strictly prohibited."
    },
    {
        title: "Feedback Utilization",
        content: "Any feedback you provide may be used by us without restrictions. You acknowledge that such feedback does not contain confidential information."
    },
    {
        title: "Third-Party Links Disclaimer",
        content: "Wave Horizons LTD is not liable for the content or practices of third-party sites linked through our services."
    },
    {
        title: "No Warranty Disclaimer",
        content: "Scriptu and its services are provided on an \"as is\" basis, without warranties of any kind. Use of our services is at your sole risk."
    }
];


export default function Terms() {

    return (
        <>
            <NextSeo
                title="Scriptu terms and conditions"
                canonical="https://www.scriptu.io/terms" // Add your canonical URL here
                openGraph={{
                    url: "https://www.scriptu.io/terms", // It's also good to set the URL in openGraph
                    title: "Scriptu Pricing | Transform Your LinkedIn Presence with AI",
                    images: [
                        {
                            url: "/logo-promo.png",
                            width: 2000,
                            height: 520,
                            alt: "Scriptu Logo",
                        },
                    ],
                    site_name: "Scriptu",
                }}
                twitter={{
                    handle: '@handle',
                    site: '@site',
                    cardType: 'summary_large_image',
                }}
                additionalLinkTags={[
                    {
                        rel: 'icon',
                        href: '/favicon.png',
                    },
                    {
                        rel: 'apple-touch-icon',
                        href: '/favicon.png',
                        sizes: '128x128',
                    },
                ]}
            />
            <div id="back-to-top-anchor"></div>
            <div style={{
                background: 'black',
                backgroundImage: 'url(/bg.jpg)',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
            }}>
                <div id="back-to-top-anchor"></div>
                <Header />
            </div>
            <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
                <SectionContainer>
                    <Typography variant="h3" sx={{ fontSize: '44px', color: '#000135', fontWeight: 700, lineHeight: 1.13, letterSpacing: '-1.76px', mt: 5, mb: 10 }}>
                        Terms and Conditions
                    </Typography>

                    {termsSections.map((section, index) => (
                        <div key={index}>
                            <Typography variant="h6" gutterBottom sx={{ fontSize: '24px', color: '#000135', fontWeight: 700, lineHeight: 1.13, letterSpacing: '-1.76px', mt: 3, textTransform: 'capitalize', textAlign: 'left' }}>
                                {index + 1}. {section.title}
                            </Typography>
                            <Typography variant="body1" gutterBottom sx={{ fontSize: '16px', color: '#000135', fontWeight: 400, textAlign: 'left', mb: 2 }}>
                                {typeof section.content === 'function' ? section.content() : section.content}
                            </Typography>
                        </div>
                    ))}
                </SectionContainer>

            </Container>
            <Footer />
            <ScrollToTop />
        </>
    );
}
